import Slider from 'rc-slider/lib/Slider';
import { HTMLProps, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetPensionCalcData } from '../../../../api/pension-robot/useGetPensionCalcData';
import { usePostDialogueData } from '../../../../api/pension-robot/usePostPensionDialogueData';
import { RobotEnvironmentContext } from '../../../../contexts/general/RobotEnvironmentContext';
import { PensionContext } from '../../../../contexts/pension-robot/PensionContext';
import { useWindowBreakpoints } from '../../../../hooks/useWindowBreakpoints';
import { isMyBND } from '../../../../utils/functions';
import { AgeInput } from '../../../general/AgeInput';

export const PlusAndMinusPensionAgeButton: React.FC<{ isPartnerPension?: boolean } & HTMLProps<HTMLDivElement>> = ({
    isPartnerPension = false,
    className,
}) => {
    const {
        parameters: { personalInfo },
        actions,
    } = useContext(PensionContext);
    const { environment } = useContext(RobotEnvironmentContext);
    const pensionCalcData = useGetPensionCalcData();
    const usePostDialogueDataQuery = usePostDialogueData();
    const pensionFactors = pensionCalcData.data?.lateEarlyRetirement?.factors;
    const [fillingAgeIn, setFillingAgeIn] = useState<number>();
    const isWindowSmallerThanLg = useWindowBreakpoints().isWindowSmallerThan('lg');
    const { t } = useTranslation('pension');

    const currentAge = isPartnerPension ? personalInfo.partner.age! : personalInfo.self.age!;
    // BND enpoint can't handle a customer that goes with pension at it's current age
    // so a person can be max e.g. 66 when going with pension at 67.
    const currentAgenPlusOne = currentAge + 1;
    const minAge = pensionFactors
        ? Math.max(currentAgenPlusOne, pensionFactors[0].targetAge ?? currentAgenPlusOne)
        : undefined;
    const maxAge = pensionFactors ? pensionFactors[pensionFactors.length - 1].targetAge : undefined;

    const changeAgeTo = (value: number | undefined) => {
        if (value === undefined) return;

        // No need to set new value as min or max allowable age is reached
        if (value < currentAgenPlusOne) return;
        isPartnerPension
            ? actions.setParameters({
                  personalInfo: { partner: { pensionAge: value } },
              })
            : actions.setParameters({
                  personalInfo: { self: { pensionAge: value } },
              });
        isMyBND(environment) && usePostDialogueDataQuery.refetch();
        setFillingAgeIn(value);
    };

    const getMinLabelSlider = () => {
        return isWindowSmallerThanLg ? `${minAge}` : `Min.\u00A0${minAge}`;
    };

    const getMaxLabelSlider = () => {
        return isWindowSmallerThanLg ? `${maxAge}` : `Max.\u00A0${maxAge}`;
    };

    const ageOfRetirering = isPartnerPension ? personalInfo.partner?.pensionAge : personalInfo.self?.pensionAge;
    return (
        <div className="flex flex-col items-start">
            <div className="mb-3 font-medium mt-5">
                {' '}
                {isPartnerPension ? t('general-pension-age-partner') : t('general-pension-age-client')}
            </div>
            <div className="flex items-center gap-4">
                <div className={'lg:w-36 w-16'}>
                    <Slider
                        max={maxAge}
                        min={minAge}
                        step={1}
                        value={ageOfRetirering}
                        onChange={(_value) => {
                            changeAgeTo(+_value);
                        }}
                        marks={{
                            [minAge!]: { label: getMinLabelSlider(), style: { transform: 'translateX(-10%)' } },
                            [maxAge!]: { label: getMaxLabelSlider(), style: { transform: 'translateX(-100%)' } },
                        }}
                    />
                </div>
                <AgeInput
                    value={fillingAgeIn ?? ageOfRetirering}
                    onBlurAction={(value) => {
                        changeAgeTo(value);
                        setFillingAgeIn(value);
                    }}
                    onChange={(value) => {
                        setFillingAgeIn(value);
                    }}
                    minAge={minAge}
                    maxAge={maxAge}
                />
            </div>
        </div>
    );
};
